import {
  Component,
  inject,
  viewChild,
  ElementRef,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { A11yModule } from '@angular/cdk/a11y';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { ThemeService } from '../../core/services/theme.service';
import { AuthService } from '../../core/services/auth.service';
import { ChangePasswordModalComponent } from '../modals/change-password-modal/change-password-modal.component';
import { BusinessGuideService } from '../../core/services/business-guide.service';

export interface SpotlightItem {
  id: string;
  title: string;
  subtitle?: string;
  icon: string;
  route?: string;
  action?: 'change_password' | 'logout' | 'toggle_guide';
  isPrimary?: boolean;
  keywords?: string[];
  requiresAdmin?: boolean;
}

export interface SpotlightSection {
  id: string;
  title: string;
  icon: string;
  items: SpotlightItem[];
}

@Component({
  selector: 'app-command-palette',
  imports: [FormsModule, MatIconModule, A11yModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(window:keydown)': 'onKeyDown($event)',
  },
  templateUrl: './command-palette.component.html',
  styleUrl: './command-palette.component.scss',
})
export class CommandPaletteComponent {
  readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');
  readonly spotlightBody = viewChild<ElementRef<HTMLDivElement>>('spotlightBody');

  private readonly dialog = inject(MatDialog);
  public readonly authService = inject(AuthService);
  public readonly guideService = inject(BusinessGuideService);
  private readonly route = inject(Router);
  public readonly themeService = inject(ThemeService);
  private readonly cdr = inject(ChangeDetectorRef);

  isSpotlightOpen = false;
  searchQuery = '';
  selectedIndex = 0;
  private isKeyboardNavigating = false;
  private lastMouseX = -1;
  private lastMouseY = -1;

  readonly allSections: SpotlightSection[] = [
    {
      id: 'wfm_planning',
      title: 'Planejamento & Operação de Escalas',
      icon: 'calendar_month',
      items: [
        {
          id: 'schedule_planning',
          title: 'Planejamento de Escalas',
          subtitle: 'Grade semanal e diária por operação, geração assistida 6x1 e validador CLT em tempo real',
          icon: 'calendar_month',
          route: '/schedule-planning',
          isPrimary: true,
          keywords: ['manual', 'grade', 'turnos', 'folga', 'clt', 'interjornada', 'editar', 'escala', 'gerar'],
        },
        {
          id: 'timecard',
          title: 'Aderência Escala x Ponto Eletrônico',
          subtitle: 'Confronto Previsto x Realizado (4 marcações diárias, Art. 58 CLT) e classificação de desvios',
          icon: 'punch_clock',
          route: '/timecard',
          keywords: ['ponto', 'aderencia', 'batidas', 'desvio', 'atraso', 'hora extra'],
        },
        {
          id: 'home_dashboard',
          title: 'Visão Geral da Operação & Matriz de Cobertura Semanal',
          subtitle: 'KPIs operacionais, cobertura Dom a Sáb e alertas de férias/aviso prévio',
          icon: 'dashboard',
          route: '/home',
          keywords: ['home', 'dashboard', 'cobertura', 'kpis', 'resumo', 'diretoria'],
        },
        {
          id: 'business_guide_faq',
          title: 'Central de Regras WFM & FAQ',
          subtitle: 'Consultar documentação trabalhista CLT, regras de negócio e FAQ (F1)',
          icon: 'help_outline',
          action: 'toggle_guide',
          keywords: ['faq', 'ajuda', 'guia', 'regras', 'f1', 'clt', 'manual'],
        },
      ],
    },
    {
      id: 'wfm_cadastros',
      title: 'Cadastros, Parametrizações & Demanda',
      icon: 'tune',
      items: [
        {
          id: 'branches',
          title: 'Operações, Diretoria/GO & Regras de Escala',
          subtitle: 'Lista de filiais BI/Protheus, mapeamento em lote e 18 parâmetros de escala',
          icon: 'storefront',
          route: '/branches',
          requiresAdmin: true,
          keywords: ['operacoes', 'filiais', 'lojas', 'diretor', 'go', 'regras', '18 campos', 'horario'],
        },
        {
          id: 'employees',
          title: 'Funcionários, Jornadas & Controle de Férias',
          subtitle: 'Quadro por Chapa, jornadas 6x1/5x2, funções Cozinha/Salão e férias RM',
          icon: 'badge',
          route: '/employees',
          keywords: ['funcionarios', 'colaboradores', 'chapa', 'jornada', '6x1', 'ferias', 'afastamentos', 'funcoes'],
        },
        {
          id: 'demand_history',
          title: 'Histórico de Pedidos, Feriados & Correlação',
          subtitle: 'Importação hora a hora, calendário de feriados e diagnóstico cadastral',
          icon: 'query_stats',
          route: '/demand-history',
          requiresAdmin: true,
          keywords: ['pedidos', 'demanda', 'historico', 'feriados', 'eventos', 'correlacao', 'importar'],
        },
      ],
    },
    {
      id: 'wfm_governance',
      title: 'Inteligência, Relatórios & Governança',
      icon: 'admin_panel_settings',
      items: [
        {
          id: 'reports',
          title: 'Central de Relatórios Gerenciais',
          subtitle: 'Exportação CSV/PDF de Escalas Planejadas, Aderência, Absenteísmo e HE',
          icon: 'assessment',
          route: '/reports',
          requiresAdmin: true,
          keywords: ['relatorios', 'exportar', 'csv', 'pdf', 'absenteismo', 'horas extras', 'indicadores'],
        },
        {
          id: 'admin',
          title: 'Administração de Usuários & Log de Auditoria',
          subtitle: 'Gestão de perfis (Admin, Gestor, Ponto), filiais e Rollback de alterações',
          icon: 'admin_panel_settings',
          route: '/admin',
          requiresAdmin: true,
          keywords: ['admin', 'usuarios', 'perfis', 'auditoria', 'logs', 'rollback', 'seguranca'],
        },
      ],
    },
  ];

  get isMac(): boolean {
    return typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);
  }

  get isAdminOrRoot(): boolean {
    return this.authService.isAdminOrRoot();
  }

  get availableSections(): SpotlightSection[] {
    const isAdmin = this.authService.isAdminOrRoot();
    const canManageSchedule = this.authService.canManageSchedule?.() ?? true;
    return this.allSections
      .map((sec) => ({
        ...sec,
        items: sec.items.filter((item) => {
          if (item.requiresAdmin && !isAdmin) return false;
          if (!canManageSchedule && item.route === '/schedule-planning') {
            return false;
          }
          return true;
        }),
      }))
      .filter((sec) => sec.items.length > 0);
  }

  get filteredSections(): SpotlightSection[] {
    const query = this.normalizeText(this.searchQuery);
    const sections: SpotlightSection[] = [];

    for (const section of this.availableSections) {
      if (!query) {
        sections.push(section);
      } else {
        const matchedItems = section.items.filter((item) => {
          const titleMatch = this.normalizeText(item.title).includes(query);
          const subtitleMatch = item.subtitle ? this.normalizeText(item.subtitle).includes(query) : false;
          const sectionMatch = this.normalizeText(section.title).includes(query);
          const keywordMatch = item.keywords?.some((k) => this.normalizeText(k).includes(query));
          return titleMatch || subtitleMatch || sectionMatch || keywordMatch;
        });

        if (matchedItems.length > 0) {
          sections.push({
            ...section,
            items: matchedItems,
          });
        }
      }
    }

    return sections;
  }

  get flatFilteredItems(): SpotlightItem[] {
    const flat: SpotlightItem[] = [];
    for (const section of this.filteredSections) {
      flat.push(...section.items);
    }
    return flat;
  }

  onKeyDown(event: KeyboardEvent): void {
    const isCmdOrCtrl = this.isMac ? event.metaKey : event.ctrlKey;

    if (event.key === 'F1') {
      event.preventDefault();
      this.guideService.toggleGuide();
      return;
    }

    if (isCmdOrCtrl && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      if (this.isSpotlightOpen) {
        this.closeSpotlight();
      } else {
        this.openSpotlight();
      }
      return;
    }

    if (this.isSpotlightOpen) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeSpotlight();
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.navigateItems(1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.navigateItems(-1);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        if (this.flatFilteredItems.length > 0 && this.flatFilteredItems[this.selectedIndex]) {
          this.executeItem(this.flatFilteredItems[this.selectedIndex]);
        }
      }
      return;
    }

    if (event.key === '/' && !this.isSpotlightOpen) {
      const activeTag = (document.activeElement?.tagName || '').toLowerCase();
      const isEditable = (document.activeElement as HTMLElement)?.isContentEditable;
      if (activeTag !== 'input' && activeTag !== 'textarea' && activeTag !== 'select' && !isEditable) {
        event.preventDefault();
        const pageSearch = document.querySelector<HTMLInputElement>(
          '.c-search-input, .search-input, input[placeholder*="Buscar"], input[placeholder*="buscar"]'
        );
        if (pageSearch) {
          pageSearch.focus();
          pageSearch.select();
        } else {
          this.openSpotlight();
        }
      }
    }
  }

  openSpotlight(): void {
    this.isSpotlightOpen = true;
    this.searchQuery = '';
    this.selectedIndex = 0;
    this.isKeyboardNavigating = false;
    this.lastMouseX = -1;
    this.lastMouseY = -1;
    setTimeout(() => {
      this.searchInput()?.nativeElement.focus();
      const bodyEl = this.spotlightBody()?.nativeElement;
      if (bodyEl) {
        bodyEl.scrollTop = 0;
      }
    }, 50);
  }

  closeSpotlight(): void {
    this.isSpotlightOpen = false;
    this.searchQuery = '';
  }

  onBackdropClick(): void {
    this.closeSpotlight();
  }

  onSearchChange(): void {
    this.selectedIndex = 0;
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.selectedIndex = 0;
    this.searchInput()?.nativeElement.focus();
  }

  navigateItems(direction: number): void {
    const total = this.flatFilteredItems.length;
    if (total === 0) return;
    this.isKeyboardNavigating = true;
    this.selectedIndex = (this.selectedIndex + direction + total) % total;
    this.cdr.detectChanges();
    this.scrollToSelectedItem();
  }

  onMouseMove(event: MouseEvent): void {
    if (this.lastMouseX !== event.clientX || this.lastMouseY !== event.clientY) {
      this.lastMouseX = event.clientX;
      this.lastMouseY = event.clientY;
      this.isKeyboardNavigating = false;
    }
  }

  onItemMouseEnter(item: SpotlightItem): void {
    if (this.isKeyboardNavigating) return;
    const index = this.flatFilteredItems.findIndex((i) => i.id === item.id);
    if (index !== -1) {
      this.selectedIndex = index;
    }
  }

  isItemSelected(item: SpotlightItem): boolean {
    if (this.flatFilteredItems.length === 0) return false;
    return this.flatFilteredItems[this.selectedIndex]?.id === item.id;
  }

  private scrollToSelectedItem(): void {
    const container = this.spotlightBody()?.nativeElement;
    if (!container) return;

    if (this.selectedIndex === 0) {
      container.scrollTop = 0;
      return;
    }

    if (this.selectedIndex === this.flatFilteredItems.length - 1) {
      container.scrollTop = container.scrollHeight;
      return;
    }

    const selectedEl = container.querySelector<HTMLElement>('.c-spotlight__item--selected');
    if (!selectedEl) return;

    const containerRect = container.getBoundingClientRect();
    const itemRect = selectedEl.getBoundingClientRect();
    const padding = 12;

    if (itemRect.top < containerRect.top + padding) {
      container.scrollTop -= Math.ceil(containerRect.top + padding - itemRect.top);
    } else if (itemRect.bottom > containerRect.bottom - padding) {
      container.scrollTop += Math.ceil(itemRect.bottom - (containerRect.bottom - padding));
    }
  }

  executeItem(item: SpotlightItem): void {
    if (!item) return;
    if (item.requiresAdmin && !this.authService.isAdminOrRoot()) return;
    this.closeSpotlight();

    if (item.action === 'toggle_guide') {
      this.guideService.toggleGuide();
    } else if (item.action === 'change_password') {
      this.onChangePassword();
    } else if (item.action === 'logout') {
      this.onLogout();
    } else if (item.route) {
      this.onRedirect(item.route);
    }
  }

  isCurrentRoute(route?: string): boolean {
    if (!route) return false;
    const currentUrl = this.route.url.split('?')[0].split('#')[0];
    return currentUrl === route || currentUrl.startsWith(route + '/');
  }

  onRedirect(route: string): void {
    this.route.navigateByUrl(route);
  }

  onChangePassword(): void {
    this.dialog.open(ChangePasswordModalComponent, {
      width: '400px',
      disableClose: true,
      panelClass: 'custom-dialog-container',
    });
  }

  onToggleTheme(): void {
    this.themeService.toggleTheme();
  }

  onLogout(): void {
    this.authService.logOut();
  }

  private normalizeText(str: string): string {
    return (str || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }
}
